/**
 * DashboardsPage — admin list of dashboards.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../lib/config";

interface DashboardItem {
  id: string;
  name: string;
  slug: string;
  is_public: boolean;
  share_token: string;
  widget_count: number;
  created_at: string;
}

async function fetchJSON<T>(url: string, options?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) throw new Error(`${res.status}`);
  return res.json() as Promise<T>;
}

export function DashboardsPage() {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState<DashboardItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [showCreate, setShowCreate] = useState(false);
  const [formName, setFormName] = useState("");
  const [formSlug, setFormSlug] = useState("");
  const [formPublic, setFormPublic] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  // Delete confirm
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      const data = await fetchJSON<DashboardItem[]>("/api/v1/dashboards/");
      setDashboards(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboards();
  }, []);

  const autoSlug = (name: string) =>
    name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);
    if (!formName.trim()) {
      setFormError("Name is required.");
      return;
    }
    setCreating(true);
    try {
      await fetchJSON("/api/v1/dashboards/", {
        method: "POST",
        body: JSON.stringify({
          name: formName.trim(),
          slug: formSlug || autoSlug(formName),
          is_public: formPublic,
        }),
      });
      setShowCreate(false);
      setFormName(""); setFormSlug(""); setFormPublic(false);
      await loadDashboards();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id: string) => {
    await apiFetch(`/api/v1/dashboards/${id}/`, { method: "DELETE" });
    setDeletingId(null);
    await loadDashboards();
  };

  const copyShareLink = (token: string) => {
    const url = `${window.location.origin}/dashboard/${token}`;
    navigator.clipboard.writeText(url).catch(() => {});
  };

  return (
    <div className="ac-page">
      <div className="ac-page-header">
        <div className="ac-page-header__left">
          <h1 className="ac-page-title">Dashboards</h1>
          <p className="ac-page-subtitle">Manage custom dashboards and share links</p>
        </div>
        <div className="ac-page-header__actions">
          <button className="aptean-btn aptean-btn--primary" onClick={() => setShowCreate(true)}>
            + Create Dashboard
          </button>
        </div>
      </div>

      {error && <div className="ac-alert ac-alert--error" style={{ marginBottom: "1rem" }}>{error}</div>}

      {loading ? (
        <div style={{ padding: "3rem", textAlign: "center", color: "var(--aptean-text-secondary)" }}>Loading dashboards...</div>
      ) : (
        <div className="ac-table-wrap">
          <table className="ac-table">
            <thead>
              <tr>
                <th style={{ width: "35%" }}>Name</th>
                <th>Slug</th>
                <th>Visibility</th>
                <th>Widgets</th>
                <th>Created</th>
                <th className="ac-th--right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {dashboards.length === 0 ? (
                <tr><td colSpan={6}><div className="ac-empty"><div className="ac-empty__icon">&#9783;</div><div className="ac-empty__title">No dashboards yet</div><div className="ac-empty__body">Create your first dashboard above.</div></div></td></tr>
              ) : dashboards.map((d) => (
                <tr key={d.id}>
                  <td>
                    <button
                      type="button"
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left" }}
                      onClick={() => navigate(`/admin/dashboards/${d.id}`)}
                    >
                      <div className="ac-row-title" style={{ color: "var(--aptean-accent)" }}>{d.name}</div>
                    </button>
                  </td>
                  <td className="ac-td--mono">{d.slug}</td>
                  <td>
                    <span className={`ac-badge ${d.is_public ? "ac-badge--info" : "ac-badge--neutral"}`}>
                      {d.is_public ? "Public" : "Private"}
                    </span>
                  </td>
                  <td className="ac-td--muted">{d.widget_count} widget{d.widget_count !== 1 ? "s" : ""}</td>
                  <td className="ac-td--muted">{new Date(d.created_at).toLocaleDateString()}</td>
                  <td className="ac-td--right ac-td--actions">
                    <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
                      {d.is_public && (
                        <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem" }} onClick={() => copyShareLink(d.share_token)} title="Copy share link">
                          Copy Link
                        </button>
                      )}
                      <button type="button" className="aptean-btn aptean-btn--ghost" style={{ fontSize: "0.75rem", padding: "0.3rem 0.75rem", color: "#a4262c", borderColor: "#f1a0a3" }} onClick={() => setDeletingId(d.id)}>
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <div className="ac-overlay" onClick={e => e.target === e.currentTarget && setShowCreate(false)}>
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Create Dashboard</h2>
              <button type="button" className="ac-dialog__close" onClick={() => setShowCreate(false)}>&#10005;</button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="ac-dialog__body">
                {formError && <div className="ac-alert ac-alert--error">{formError}</div>}
                <div className="ac-field">
                  <label>Name *</label>
                  <input
                    className="aptean-input"
                    value={formName}
                    onChange={(e) => {
                      setFormName(e.target.value);
                      if (!formSlug) setFormSlug(autoSlug(e.target.value));
                    }}
                    placeholder="e.g. Platform Health"
                    required
                  />
                </div>
                <div className="ac-field">
                  <label>Slug</label>
                  <input className="aptean-input" value={formSlug} onChange={(e) => setFormSlug(e.target.value)} placeholder="e.g. platform-health" />
                </div>
                <label className="ac-checkbox-item" style={{ padding: "0.25rem 0" }}>
                  <input type="checkbox" checked={formPublic} onChange={(e) => setFormPublic(e.target.checked)} />
                  Public (accessible without login via share link)
                </label>
              </div>
              <div className="ac-dialog__footer">
                <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setShowCreate(false)}>Cancel</button>
                <button type="submit" className="aptean-btn aptean-btn--primary" disabled={creating}>{creating ? "Creating..." : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deletingId && (
        <div className="ac-overlay">
          <div className="ac-dialog">
            <div className="ac-dialog__header">
              <h2 className="ac-dialog__title">Confirm Delete</h2>
            </div>
            <div className="ac-dialog__body">
              <div className="ac-alert ac-alert--warning">Delete this dashboard? This cannot be undone.</div>
            </div>
            <div className="ac-dialog__footer">
              <button type="button" className="aptean-btn aptean-btn--ghost" onClick={() => setDeletingId(null)}>Cancel</button>
              <button type="button" className="aptean-btn aptean-btn--primary" style={{ background: "#a4262c", borderColor: "#a4262c" }} onClick={() => handleDelete(deletingId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
